import React from 'react';
import { useUserContext } from '../../context/useUserContext';
import GrantedPrivilegesContext from './GrantedPrivilegesContext';
import { useGetGrantedPrivilegesQuery } from '../../../graphql/policy.generated';
import { ErrorSection } from '../../shared/error/ErrorSection';
import { ComponentSpinner } from '../../shared/ComponentSpinner';

interface Props {
    children: React.ReactNode;
}

/**
 * Fetches getGrantedPrivileges for the given resource (entityType + urn) and
 * provides them via GrantedPrivilegesContext so children can use useGrantedPrivileges().
 *
 * When entityType is omitted, the query is sent without a resourceSpec, which
 * returns platform-level privileges (used for pages like Ingestion).
 */
export default function GrantedPrivilegesProvider({ children }: Props) {
    const authenticatedUserUrn = useUserContext()?.user?.urn;
    const { loading, error, data } = useGetGrantedPrivilegesQuery({
        variables: {
            input: {
                actorUrn: authenticatedUserUrn as string,
            },
        },
        errorPolicy: 'all',
        skip: !authenticatedUserUrn,
        fetchPolicy: 'cache-first',
    });

    const privileges = data?.getGrantedPrivileges?.privileges ?? [];

    if (error) return <ErrorSection />;
    if (loading && !data) return <ComponentSpinner />;

    return <GrantedPrivilegesContext.Provider value={privileges}>{children}</GrantedPrivilegesContext.Provider>;
}
